import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getAssetDecimals } from "@/lib/marketUtils";
import { getLastOrder, requestAmountPrefill } from "@/lib/lastOrder";
import type { Market } from "@/lib/wsTypes";
import type { OrderType, TradeSide } from "./useTradingViewState";

/**
 * Keyboard shortcuts for the trading view. None of these place an order --
 * user funds are at stake, so every order goes through an explicit Enter (or
 * CTA click) after the ticket is visibly filled:
 *
 * - `b` / `s`       switch the ticket to buy / sell and focus the amount
 * - `ctrl+1..5`     switch to the 1st..5th market (browser willing: some
 *                   browsers reserve ctrl+digit for tab switching)
 * - `r`             FILL the ticket with the last order placed in this tab;
 *                   review, then Enter to place
 * - `shift+R`       FILL the ticket with the INVERSE of the last order: side
 *                   flipped, quantity converted across the fill price (bids
 *                   are quote-denominated, asks base-denominated), as a
 *                   market order -- "sell what I just bought", pending Enter
 */

interface UseTradingHotkeysArgs {
  markets: readonly Market[];
  selectMarket: (market: Market) => void;
  setTradeSide: (side: TradeSide) => void;
  setOrderType: (type: OrderType) => void;
  setLimitPrice: (price: string) => void;
}

export interface TradingHotkeysState {
  /** Whether the trading hotkeys are active (`?` and Escape always work). */
  hotkeysEnabled: boolean;
  setHotkeysEnabled: (enabled: boolean) => void;
  cheatsheetOpen: boolean;
  setCheatsheetOpen: (open: boolean) => void;
}

const HOTKEYS_ENABLED_KEY = "sr-hotkeys-enabled";

function readHotkeysEnabled(): boolean {
  try {
    return localStorage.getItem(HOTKEYS_ENABLED_KEY) !== "false";
  } catch {
    return true;
  }
}

/** True when the event originates in a context where letters mean typing.
 * Numeric-only inputs (amount, limit price) can never take a letter, so the
 * letter hotkeys stay live there. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLInputElement) {
    if (target.inputMode === "decimal" || target.inputMode === "numeric") return false;
    return !["number", "range", "checkbox", "radio", "button", "submit"].includes(target.type);
  }
  const tag = target.tagName;
  return tag === "TEXTAREA" || tag === "SELECT";
}

/** Focus the trade ticket's amount input. Deferred twice: a side-change
 * hotkey remounts the SwapPanel (it is keyed on the side), so the input this
 * queries for does not exist until after the next render commit. */
function focusAmountInput(): void {
  requestAnimationFrame(() => {
    setTimeout(() => {
      const input = document.querySelector<HTMLInputElement>("[data-hotkey-amount] input");
      input?.focus();
      input?.select();
    }, 0);
  });
}

/** Raw smallest-unit quantity -> the decimal string the amount field shows. */
function rawToDisplay(raw: number, decimals: number): string {
  const q = BigInt(Math.max(0, Math.round(raw)));
  const scale = 10n ** BigInt(decimals);
  const whole = q / scale;
  const frac = (q % scale).toString().padStart(decimals, "0").replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : whole.toString();
}

export function useTradingHotkeys({
  markets,
  selectMarket,
  setTradeSide,
  setOrderType,
  setLimitPrice,
}: UseTradingHotkeysArgs): TradingHotkeysState {
  const [hotkeysEnabled, setHotkeysEnabledState] = useState(readHotkeysEnabled);
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
  const setHotkeysEnabled = (enabled: boolean) => {
    setHotkeysEnabledState(enabled);
    try {
      localStorage.setItem(HOTKEYS_ENABLED_KEY, String(enabled));
    } catch {
      /* preference just won't persist */
    }
  };

  // The handler is registered once; everything mutable is read through a ref
  // so market switches don't churn the window listener.
  const stateRef = useRef({ markets, selectMarket, setTradeSide, setOrderType, setLimitPrice, hotkeysEnabled, cheatsheetOpen });
  stateRef.current = { markets, selectMarket, setTradeSide, setOrderType, setLimitPrice, hotkeysEnabled, cheatsheetOpen };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const { markets, selectMarket, setTradeSide, setOrderType, setLimitPrice, hotkeysEnabled, cheatsheetOpen } = stateRef.current;
      const key = event.key.toLowerCase();
      const ctrl = event.ctrlKey || event.metaKey;

      // ? toggles the cheatsheet and Escape closes it -- always live (even with
      // hotkeys disabled, so the toggle inside the sheet stays reachable).
      if (event.key === "?" && !ctrl && !event.altKey && !isTyping(event.target)) {
        event.preventDefault();
        setCheatsheetOpen(!cheatsheetOpen);
        return;
      }
      if (event.key === "Escape" && cheatsheetOpen) {
        event.preventDefault();
        setCheatsheetOpen(false);
        return;
      }

      if (!hotkeysEnabled) return;

      // b / s -- switch side, focus the amount.
      if (!ctrl && !event.altKey && (key === "b" || key === "s") && !isTyping(event.target)) {
        event.preventDefault();
        setTradeSide(key === "b" ? "buy" : "sell");
        focusAmountInput();
        return;
      }

      // ctrl+1..5 -- market switcher, in the order the market bar lists them.
      if (ctrl && !event.shiftKey && !event.altKey && key >= "1" && key <= "5") {
        const market = markets[Number(key) - 1];
        if (market) {
          event.preventDefault();
          selectMarket(market);
        }
        return;
      }

      // r / shift+R -- fill the ticket with the last order / its inverse.
      if (!ctrl && !event.altKey && key === "r" && !isTyping(event.target)) {
        event.preventDefault();
        const last = getLastOrder();
        if (!last) {
          toast.info("No order placed this session to repeat.");
          return;
        }
        const market = markets.find((m) => m.key === last.marketKey);
        if (!market) {
          toast.error("The last order's market is not available.");
          return;
        }

        const inverse = event.shiftKey;
        const side: "Bid" | "Ask" = inverse
          ? (last.request.side === "Bid" ? "Ask" : "Bid")
          : last.request.side;
        // Bid quantities are quote-denominated, ask quantities base-denominated,
        // so an inverse ("sell what I just bought") converts across the price.
        let rawQuantity = last.request.quantity;
        if (inverse) {
          const price = Number(last.request.price);
          if (!Number.isFinite(price) || price <= 0) {
            toast.error("Last order has no usable price to invert.");
            return;
          }
          rawQuantity = last.request.side === "Bid"
            ? last.request.quantity / price
            : last.request.quantity * price;
          if (Math.round(rawQuantity) <= 0) {
            toast.error("Inverse quantity rounds to zero.");
            return;
          }
        }
        const spendAsset = side === "Ask" ? market.tradingPair.base : market.tradingPair.quote;

        selectMarket(market);
        setTradeSide(side === "Ask" ? "sell" : "buy");
        // An inverse is an exit priced at the current book, so it fills as a
        // market order; a straight repeat keeps the original type and price.
        setOrderType(inverse ? "market" : last.orderType);
        if (!inverse && last.orderType === "limit") setLimitPrice(last.request.price);
        requestAmountPrefill(rawToDisplay(rawQuantity, getAssetDecimals(spendAsset)));
        focusAmountInput();
        toast.info(
          inverse
            ? "Ticket filled with the inverse of your last order — Enter to place."
            : "Ticket filled with your last order — Enter to place.",
        );
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return { hotkeysEnabled, setHotkeysEnabled, cheatsheetOpen, setCheatsheetOpen };
}
